import { X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { fmtRelative, fmtTimestamp } from '../format.js';
import { Button } from './Button.js';
import { EmptyState } from './EmptyState.js';
import { StatusDot, type StatusTone } from './StatusDot.js';

export type NotificationTone = 'ok' | 'warn' | 'info' | 'brand' | 'dim';

export interface NotificationAction {
  label: string;
  onSelect: () => void;
}

export interface Notification {
  id: string;
  title: string;
  description?: string;
  tone: NotificationTone;
  /** ISO timestamp of creation. */
  at: string;
  action?: NotificationAction;
  read?: boolean;
  /** Optional account scoping label shown in mono. */
  scope?: string;
}

export interface NotifyInput {
  id?: string;
  title: string;
  description?: string;
  tone?: NotificationTone;
  action?: NotificationAction;
  scope?: string;
  /** Auto-dismiss in ms for the transient toast. 0 keeps it until dismissed. Default 6000 (0 for brand tone). */
  duration?: number;
}

export interface ToastContextValue {
  notify: (input: NotifyInput) => string;
  dismiss: (id: string) => void;
  /** All notifications kept for the NotificationCenter (most recent first). */
  notifications: Notification[];
  markAllRead: () => void;
  clear: () => void;
  /** Transient toasts currently on screen. */
  toasts: Notification[];
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Maximum notifications retained. Default 100. */
  limit?: number;
  /** Position of the transient stack. Default bottom-right. */
  position?: 'bottom-right' | 'top-right';
}

let counter = 0;

/** Holds notifications and renders the transient toast stack (hairline panels with a tone dot). */
export function ToastProvider({ children, limit = 100, position = 'bottom-right' }: ToastProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [toastIds, setToastIds] = useState<string[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setToastIds((ids) => ids.filter((x) => x !== id));
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);

  const notify = useCallback(
    (input: NotifyInput) => {
      counter += 1;
      const id = input.id ?? `n-${Date.now()}-${counter}`;
      const tone = input.tone ?? 'info';
      const n: Notification = {
        id,
        title: input.title,
        description: input.description,
        tone,
        at: new Date().toISOString(),
        action: input.action,
        scope: input.scope,
        read: false,
      };
      setNotifications((list) => [n, ...list.filter((x) => x.id !== id)].slice(0, limit));
      setToastIds((ids) => [id, ...ids.filter((x) => x !== id)].slice(0, 4));
      const duration = input.duration ?? (tone === 'brand' ? 0 : 6000);
      if (duration > 0) {
        const t = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, t);
      }
      return id;
    },
    [dismiss, limit],
  );

  const markAllRead = useCallback(() => setNotifications((list) => list.map((n) => (n.read ? n : { ...n, read: true }))), []);
  const clear = useCallback(() => {
    setNotifications([]);
    setToastIds([]);
    timers.current.forEach((t) => clearTimeout(t));
    timers.current.clear();
  }, []);

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((t) => clearTimeout(t));
      map.clear();
    };
  }, []);

  const toasts = useMemo(() => toastIds.map((id) => notifications.find((n) => n.id === id)).filter((n): n is Notification => Boolean(n)), [toastIds, notifications]);
  const value = useMemo<ToastContextValue>(() => ({ notify, dismiss, notifications, markAllRead, clear, toasts }), [notify, dismiss, notifications, markAllRead, clear, toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="region"
        aria-live="polite"
        aria-label="Notifications"
        className={cn('pointer-events-none fixed z-[110] flex w-[360px] max-w-[calc(100vw-32px)] flex-col gap-2', position === 'bottom-right' ? 'bottom-4 right-4' : 'right-4 top-4')}
      >
        {toasts.map((n) => (
          <ToastItem key={n.id} notification={n} onDismiss={() => dismiss(n.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const TONE_LABEL: Record<NotificationTone, string> = { ok: 'OK', warn: 'Warning', info: 'Info', brand: 'Attention', dim: 'Note' };

function ToastItem({ notification: n, onDismiss }: { notification: Notification; onDismiss: () => void }) {
  return (
    <div role={n.tone === 'brand' ? 'alert' : 'status'} className="pointer-events-auto relative rounded-sm border border-line bg-panel px-4 py-3 pl-5 text-ink">
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-[2px]', toneBar(n.tone))} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <StatusDot tone={n.tone} label={TONE_LABEL[n.tone]} tintLabel={n.tone !== 'dim'} />
          <p className="mt-1 text-nav font-medium tracking-tight">{n.title}</p>
          {n.description ? <p className="mt-0.5 text-xs leading-snug text-mute">{n.description}</p> : null}
          {n.action ? (
            <div className="mt-2">
              <Button variant="ghost" size="sm" onClick={n.action.onSelect}>
                {n.action.label}
              </Button>
            </div>
          ) : null}
        </div>
        <button type="button" aria-label="Dismiss" onClick={onDismiss} className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-mute transition-colors hover:bg-card hover:text-ink">
          <X size={12} aria-hidden />
        </button>
      </div>
    </div>
  );
}

function toneBar(tone: StatusTone): string {
  switch (tone) {
    case 'ok':
      return 'bg-ok';
    case 'warn':
      return 'bg-warn';
    case 'info':
      return 'bg-info';
    case 'brand':
      return 'bg-brand';
    default:
      return 'bg-dim';
  }
}

export interface NotificationCenterProps {
  items: Notification[];
  /** Reference time for relative stamps; defaults to now. */
  now?: string | number | Date;
  onMarkAllRead?: () => void;
  onClear?: () => void;
  onSelect?: (n: Notification) => void;
  emptyMessage?: string;
  /** Fixed height for the scroll area. Default 360. */
  height?: number;
  className?: string;
}

/** List of notifications for a panel or popover: dot, title, description, mono timestamp. */
export function NotificationCenter({ items, now, onMarkAllRead, onClear, onSelect, emptyMessage = 'No notifications.', height = 360, className }: NotificationCenterProps) {
  const unread = items.filter((n) => !n.read).length;
  return (
    <div className={cn('flex flex-col rounded-sm border border-line bg-panel', className)}>
      <header className="flex items-center justify-between gap-3 border-b border-hair px-4 py-2.5">
        <p className="label-mono">
          Notifications <span className="text-dim">·</span> {unread} unread
        </p>
        <div className="flex items-center gap-3">
          {onMarkAllRead ? (
            <Button variant="ghost" size="sm" trailingArrow={false} onClick={onMarkAllRead} disabled={unread === 0}>
              Mark all read
            </Button>
          ) : null}
          {onClear ? (
            <Button variant="ghost" size="sm" trailingArrow={false} onClick={onClear} disabled={items.length === 0}>
              Clear
            </Button>
          ) : null}
        </div>
      </header>
      <div className="scrollbar-thin overflow-y-auto" style={{ maxHeight: height }}>
        {items.length === 0 ? (
          <EmptyState message={emptyMessage} compact />
        ) : (
          <ul>
            {items.map((n) => {
              const inner = (
                <>
                  <span className="flex items-center justify-between gap-3">
                    <StatusDot tone={n.tone} label={n.scope ?? TONE_LABEL[n.tone]} tintLabel={n.tone !== 'dim'} />
                    <span className="font-mono text-2xs text-mute" title={fmtTimestamp(n.at)}>
                      {fmtRelative(n.at, now)}
                    </span>
                  </span>
                  <span className={cn('mt-1 block text-nav tracking-tight', n.read ? 'text-ink/80' : 'font-medium text-ink')}>{n.title}</span>
                  {n.description ? <span className="mt-0.5 block text-xs leading-snug text-mute">{n.description}</span> : null}
                </>
              );
              return (
                <li key={n.id} className="relative border-b border-hair last:border-b-0">
                  {!n.read ? <span aria-hidden className="absolute inset-y-2 left-0 w-[2px] bg-brand" /> : null}
                  {onSelect ? (
                    <button type="button" onClick={() => onSelect(n)} className="block w-full px-4 py-2.5 text-left transition-colors hover:bg-card focus-visible:outline-offset-[-1px]">
                      {inner}
                    </button>
                  ) : (
                    <div className="px-4 py-2.5">{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
